/**
 * Ledger > journal entry detail (tracker item 31).
 *
 * Lines with account, debit, credit and customer; a link to the posting
 * document (invoice / payment) or, for reversals, to the related entry; and a
 * "Reverse" action for manual ADJUSTMENT entries that are not a reversal and
 * have not been reversed (POST /gl/journal-entries/:id/reverse). Other entries
 * say why they cannot be reversed here. After any reverse attempt the entry
 * is re-read, so a 409 (someone else reversed it first) shows the new link.
 *
 * Inventory receipt / count adjustment entries cannot be reversed (backend
 * 400 INVENTORY_ENTRY_NOT_REVERSIBLE, tracker item 30). They are recognised
 * by their memo and item sourceId (ledgerUi.isLikelyInventoryEntry); if the
 * server refuses one the heuristic missed, Reverse is hidden for this entry.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { createAPIClient, IJournalEntriesApi } from '../api';
import * as types from '../api/types';
import { getErrorCode, getErrorMessage } from '../utils/apiError';
import { inputClass, labelClass, secondaryButtonClass } from '../components/formStyles';
import { ReversalBadges } from './LedgerListPage';
import {
  INVENTORY_ENTRY_NOT_REVERSIBLE_MESSAGE,
  LEDGER_PATH,
  REVERSAL_MEMO_MAX_LENGTH,
  createdByLabel,
  entryTotalCents,
  ledgerDate,
  ledgerEntryPath,
  ledgerMoney,
  lineAmount,
  reversalBlockReason,
  sourceDocumentLink,
  sourceTypeLabel,
} from './ledgerUi';
import { sumDecimals } from '../utils/cents';
import { Toast } from '../components/Toast';

const dangerButtonClass =
  'bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white text-sm font-semibold py-2.5 sm:py-2 px-4 rounded-md transition duration-200';

export function LedgerDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const api = useMemo<IJournalEntriesApi>(() => createAPIClient(), []);
  const [entry, setEntry] = useState<types.JournalEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [confirming, setConfirming] = useState(false);
  const [memo, setMemo] = useState('');
  const [reversing, setReversing] = useState(false);
  const [reverseError, setReverseError] = useState<string | null>(null);
  const [reversal, setReversal] = useState<types.JournalEntry | null>(null);
  /** The server refused the reversal as an inventory entry (400 INVENTORY_ENTRY_NOT_REVERSIBLE). */
  const [inventoryLocked, setInventoryLocked] = useState(false);

  const load = useCallback(async () => {
    const loaded = await api.getJournalEntry(id);
    setEntry(loaded);
    return loaded;
  }, [api, id]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(null);
    setEntry(null);
    setConfirming(false);
    setReverseError(null);
    setReversal(null);
    setInventoryLocked(false);
    api.getJournalEntry(id)
      .then((loaded) => { if (active) setEntry(loaded); })
      .catch((cause) => { if (active) setLoadError(getErrorMessage(cause, 'Could not load the journal entry')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [api, id]);

  async function handleReverse() {
    if (!entry || reversing) return;
    setReversing(true);
    setReverseError(null);
    try {
      const created = await api.reverseJournalEntry(entry.id, memo);
      setReversal(created);
      setConfirming(false);
      setMemo('');
    } catch (cause) {
      // e.g. 409 ALREADY_REVERSED when someone else reversed it first
      if (getErrorCode(cause) === 'INVENTORY_ENTRY_NOT_REVERSIBLE') {
        // Shown once, in the "Not reversible here" box, which replaces the button.
        setInventoryLocked(true);
      } else {
        setReverseError(getErrorMessage(cause, 'Could not reverse the journal entry'));
      }
      setConfirming(false);
    } finally {
      // Re-read either way so the button and the "Reversed by" link reflect the server.
      try { await load(); } catch { /* keep the entry already shown */ }
      setReversing(false);
    }
  }

  if (loading) return <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"><p>Loading journal entry…</p></main>;
  if (loadError || !entry) {
    return (
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to={LEDGER_PATH} className="text-sm font-semibold text-gray-500 hover:text-blue-600">← Ledger</Link>
        {loadError && <Toast kind="error">{loadError}</Toast>}<p className="mt-4 text-gray-700">{loadError ? 'The journal entry could not be loaded.' : 'Journal entry not found'}</p>
      </main>
    );
  }

  const sourceLink = sourceDocumentLink(entry);
  const blockReason = reversalBlockReason(entry) ?? (inventoryLocked ? INVENTORY_ENTRY_NOT_REVERSIBLE_MESSAGE : null);
  const debitTotal = sumDecimals(entry.lines.map((line) => line.debit));
  const creditTotal = sumDecimals(entry.lines.map((line) => line.credit));

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Link to={LEDGER_PATH} className="text-sm font-semibold text-gray-500 hover:text-blue-600">← Ledger</Link>
      <div className="mt-1 mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="min-w-0 break-words text-2xl font-bold text-gray-900">
          Journal entry {ledgerDate(entry.entryDate)}
          <ReversalBadges entry={entry} />
        </h1>
        {!blockReason && !confirming && (
          <button type="button" className={dangerButtonClass} disabled={reversing} onClick={() => { setConfirming(true); setReverseError(null); }}>
            Reverse
          </button>
        )}
      </div>

      {reversal && (
        <Toast kind="success" id={`reversal-${reversal.id}`}>
          Reversal posted. <Link to={ledgerEntryPath(reversal.id)} className="font-semibold underline">View the reversing entry</Link>.
        </Toast>
      )}
      {reverseError && <Toast kind="error">{reverseError}</Toast>}

      {confirming && (
        <section role="dialog" aria-modal="false" aria-labelledby="reverse-title" className="mb-6 rounded-lg border border-red-200 bg-white p-5 shadow">
          <h2 id="reverse-title" className="text-base font-semibold text-gray-900">Reverse this entry?</h2>
          <p className="mt-1 text-sm text-gray-600">
            This posts a new adjustment dated today with every debit and credit swapped ({ledgerMoney(entryTotalCents(entry))}).
            The original entry stays in the ledger. An entry can be reversed only once.
          </p>
          <div className="mt-4 max-w-xl">
            <label htmlFor="reverse-memo" className={labelClass}>Memo (optional)</label>
            <textarea
              id="reverse-memo"
              className={inputClass}
              rows={2}
              maxLength={REVERSAL_MEMO_MAX_LENGTH}
              value={memo}
              placeholder={`Reversal of entry ${entry.id}`}
              onChange={(event) => setMemo(event.target.value)}
              disabled={reversing}
            />
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            <button type="button" className={dangerButtonClass} disabled={reversing} onClick={handleReverse}>
              {reversing ? 'Reversing…' : 'Confirm reversal'}
            </button>
            <button type="button" className={secondaryButtonClass} disabled={reversing} onClick={() => { setConfirming(false); setMemo(''); }}>
              Cancel
            </button>
          </div>
        </section>
      )}

      <section className="bg-white rounded-lg shadow p-5 sm:p-6 mb-6">
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3 text-sm">
          <div><dt className="text-gray-500">Entry date</dt><dd className="font-medium text-gray-900">{ledgerDate(entry.entryDate)}</dd></div>
          <div><dt className="text-gray-500">Source</dt><dd className="font-medium text-gray-900">
            {sourceTypeLabel(entry.sourceType)}
            {sourceLink && <> · <Link to={sourceLink.to} className="text-blue-700 hover:underline">{sourceLink.label}</Link></>}
          </dd></div>
          <div><dt className="text-gray-500">Created by</dt><dd className="font-medium text-gray-900">{createdByLabel(entry)}</dd></div>
          <div className="sm:col-span-3"><dt className="text-gray-500">Memo</dt><dd className="break-words text-gray-900">{entry.memo || '—'}</dd></div>
          {entry.reversedByEntryId && (
            <div className="sm:col-span-3"><dt className="text-gray-500">Reversed by</dt><dd>
              <Link to={ledgerEntryPath(entry.reversedByEntryId)} className="text-blue-700 hover:underline">View reversing entry</Link>
            </dd></div>
          )}
        </dl>
        {blockReason && (
          <p className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700" data-testid="not-reversible">
            <span className="font-semibold">Not reversible here:</span> {blockReason}
          </p>
        )}
      </section>

      <section className="bg-white rounded-lg shadow p-5 sm:p-6">
        <div className="overflow-x-auto">
          <table className="responsive-table min-w-full divide-y divide-gray-200 text-sm">
            <caption className="sr-only">Journal lines</caption>
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-4 py-3 text-left font-medium text-gray-600">Account</th>
                <th scope="col" className="px-4 py-3 text-right font-medium text-gray-600">Debit</th>
                <th scope="col" className="px-4 py-3 text-right font-medium text-gray-600">Credit</th>
                <th scope="col" className="px-4 py-3 text-left font-medium text-gray-600">Customer</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {entry.lines.map((line) => (
                <tr key={line.id}>
                  <td className="px-4 py-3" data-label="Account" data-primary>
                    <span className="font-mono font-semibold">{line.accountNumber}</span> · {line.accountName}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums" data-label="Debit">{lineAmount(line.debit)}</td>
                  <td className="px-4 py-3 text-right tabular-nums" data-label="Credit">{lineAmount(line.credit)}</td>
                  <td className="px-4 py-3" data-label="Customer">
                    {line.customerId ? <Link to={`/customers/${encodeURIComponent(line.customerId)}`} className="text-blue-700 hover:underline">View customer</Link> : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50 font-semibold">
              <tr>
                <td className="px-4 py-3" data-label="Account" data-primary>Total</td>
                <td className="px-4 py-3 text-right tabular-nums" data-label="Debit">{ledgerMoney(debitTotal)}</td>
                <td className="px-4 py-3 text-right tabular-nums" data-label="Credit">{ledgerMoney(creditTotal)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      </section>
    </main>
  );
}
